/**
 * `<HelpFillScope formId>` — where the shared controls of a reviewed form register themselves as help fill targets
 * (owner request 2026-10-18; ADR 0072; design §31.9.5 amended).
 *
 * Placed only around a reviewed form (the `help-registry` arch rule keeps it to the reviewed form files) of a form that
 * takes component fields (`componentField: true`). Inside it, `@wetalk/ui`'s `Input`, `Select` and `Segmented` hand
 * this scope their name, label, kind, choices and their own setter (`HelpControlContext`); the scope keeps those the
 * registry allows (`isComponentFieldCodeAllowed`: never a code naming a never-actionable category, never one the form
 * binds itself through `useHelpFillable`) and registers them with the fill registry as ONE registration of `formId`,
 * each port carrying its spec. A fill then sets each through the control's own native change, exactly as typing does.
 *
 * Outside the fill provider (every form suite, the operator console) it renders its children and nothing else. It
 * sends nothing (`help-fill-no-network`).
 */
import {
  HELP_FORM_REGISTRY,
  isComponentFieldCodeAllowed,
  type FillableFormSpec,
  type HelpFillValue,
  type HelpFormId,
} from "@wetalk/domain";
import { HELP_FORM_MARK_ATTRIBUTE } from "@wetalk/help-widget/path";
import { HelpControlContext, type HelpControlPort, type HelpControlSpec } from "@wetalk/ui";
import { use, useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

import { HelpFillContext } from "./fill-context.js";
import { sameFillValue, type HelpFieldPort } from "./fill-registry.js";

function noSubscription(): () => void {
  return () => undefined;
}

function noSnapshot(): number {
  return 0;
}

/** What a component's value may be, by its kind, beyond the registry's parse: its ceiling, its offered choices. */
function acceptsOf(spec: HelpControlSpec, value: HelpFillValue): boolean {
  switch (spec.kind) {
    case "text":
      return typeof value === "string" && (spec.maxLength === undefined || Array.from(value).length <= spec.maxLength);
    case "single_choice":
      return typeof value === "string" && (spec.option ?? []).some((one) => one.value === value);
    case "toggle":
      return typeof value === "boolean";
    case "whole_number":
      return typeof value === "number";
    case "local_time":
    case "local_date":
      return typeof value === "string";
  }
}

function portOf(spec: HelpControlSpec): HelpFieldPort {
  return {
    read: () => spec.read(),
    accepts: (value) => acceptsOf(spec, value),
    set: (value) => {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") spec.set(value);
    },
    unset: null,
    spec: () => ({
      code: spec.name,
      kind: spec.kind,
      label: spec.label,
      ...(spec.option === undefined ? {} : { option: spec.option.map((one) => one.value), optionLabel: spec.option.map((one) => one.label) }),
    }),
  };
}

export function HelpFillScope({ formId, children }: { readonly formId: HelpFormId; readonly children: ReactNode }): ReactNode {
  const context = use(HelpFillContext);
  const registry = context === null ? null : context.registry;
  const href = context === null ? "" : context.href;
  const owner = useId();
  const control = useRef(new Map<string, HelpControlSpec>());
  const [version, setVersion] = useState(0);
  const takesComponents = (HELP_FORM_REGISTRY[formId] as FillableFormSpec).componentField === true;

  // A fill or an undo re-renders the scope's controls, so their marks follow.
  const snapshot = useSyncExternalStore(registry === null ? noSubscription : registry.subscribe, registry === null ? noSnapshot : registry.snapshot, noSnapshot);

  // Stable for the scope's life: a control registers once per mount, whatever re-renders.
  const register = useCallback<HelpControlPort["register"]>(
    (spec) => {
      if (!isComponentFieldCodeAllowed(formId, spec.name)) return () => undefined;
      control.current.set(spec.name, spec);
      setVersion((now) => now + 1);
      return () => {
        if (control.current.get(spec.name) !== spec) return;
        control.current.delete(spec.name);
        setVersion((now) => now + 1);
      };
    },
    [formId],
  );

  // A new object after each fill or undo (`snapshot`), so the controls re-render and re-read their marks.
  const port = useMemo<HelpControlPort | null>(() => {
    if (registry === null || !takesComponents) return null;
    return {
      register,
      markOf(name) {
        const spec = control.current.get(name);
        if (spec === undefined) return {};
        const filled = registry.filledValue(formId, name);
        return filled !== undefined && sameFillValue(spec.read(), filled)
          ? { [HELP_FORM_MARK_ATTRIBUTE.field]: name, [HELP_FORM_MARK_ATTRIBUTE.filled]: "true" }
          : { [HELP_FORM_MARK_ATTRIBUTE.field]: name };
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `snapshot` is the point: marks follow every fill.
  }, [registry, formId, takesComponents, register, snapshot]);

  useEffect(() => {
    if (registry === null || !takesComponents || control.current.size === 0) return undefined;
    const field: Record<string, HelpFieldPort> = {};
    for (const [name, spec] of control.current) field[name] = portOf(spec);
    return registry.register({ owner, formId, href, field });
  }, [registry, takesComponents, owner, formId, href, version]);

  return port === null ? children : <HelpControlContext value={port}>{children}</HelpControlContext>;
}
